import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { API, api } from '@unboared/react';

/**
 * Hook that attaches Unboared context (clientID, sessionID, deviceID) to all Sentry events.
 * This allows filtering errors by client/session/device in the Sentry dashboard.
 *
 * Safe to call before or after the API is initialized — guards against undefined api().
 */
export function useSentryContext({
    clientID,
    deviceType,
    sessionID,
}: {
    clientID: string | null;
    deviceType: 'screen' | 'gamepad';
    sessionID?: string;
}) {
    // Set static tags immediately
    useEffect(() => {
        Sentry.setTag('deviceType', deviceType);
        if (clientID) {
            Sentry.setTag('clientID', clientID);
        }
        if (sessionID) {
            Sentry.setTag('sessionID', sessionID);
        }
    }, [clientID, deviceType, sessionID]);

    // Set dynamic tags once the API is ready (sessionID, deviceID, player info)
    useEffect(() => {
        // Guard: API may not be initialized yet if called before UnboaredGame renders
        if (!api()) return;

        const unsubReady = API.onReady(() => {
            const sid = API.getSessionID();
            const did = API.getDeviceID();

            Sentry.setTag('sessionID', sid);
            Sentry.setTag('deviceID', did);
            Sentry.setUser({
                id: did,
                username: API.getPlayer?.(did)?.username,
            });

            Sentry.addBreadcrumb({
                category: 'unboared',
                message: `Session ready: sessionID=${sid}, deviceID=${did}`,
                level: 'info',
            });
        });
        return unsubReady;
    }, []);
}
